import { SidebarItemType } from "../../js/enums";
import {
  getNavigationItemDetails,
  getNavigationItemToAlwaysDisplayDetails,
} from "../../js/navigationItems";
import { checkForValidRoles } from "../form/Helpers";

const navigationItemsToAlwaysShow = [
  "/admin-settings",
  "/global-settings",
  "/search",
];

const settingsManagedPageIds = [
  "g4c_communicationoptions",
  "sitemap",
];

function isSettingsManagedPage(page) {
  return page && page.Id && settingsManagedPageIds.includes(page.Id);
}

function isExactActiveLink(pathname, link) {
  return link.identifiers
    ? link.identifiers.some((x) => pathname === x)
    : pathname === link.to;
}

function isActiveLink(pathname, link) {
  return link.identifiers
    ? link.identifiers.some((x) => pathname.startsWith(x))
    : pathname.startsWith(link.to);
}

function checkLinks(
  pathname,
  links,
  itemId,
  subItemId = "",
  isExact
) {
  if (links) {
    for (const link of links) {
      let result = checkLink(
        pathname,
        link,
        itemId,
        subItemId,
        isExact
      );
      if (result) {
        return result;
      }
    }
  }
  return null;
}

function checkLink(pathname, link, itemId, subItemId = "", isExact) {
  const isActive = isExact
    ? isExactActiveLink(pathname, link)
    : isActiveLink(pathname, link);
  if (isActive) {
    return {
      display: itemId,
      subDisplay: subItemId,
      openInNewTab: link.openInNewTab,
      workspaceId: link.workspaceId,
      reportId: link.reportId,
      listName: link.listName,
      recordName: link.recordName,
      allowedRoles: link.allowedRoles,
      title: link.text,
      url: link.url,
    };
  }
  return null;
}

function findMatchingLink(pathname, navigationItems, isExact) {
  //Check through the navigation items for a matching link
  for (const item of navigationItems) {
    if (
      item.itemType === SidebarItemType.Page &&
      item.id &&
      item.link
    ) {
      const result = checkLink(
        pathname,
        item.link,
        item.id,
        null,
        isExact
      );
      if (result) {
        return result;
      }
    } else if (
      item.itemType === SidebarItemType.Section &&
      item.id &&
      item.subItems &&
      item.subItems.length > 0
    ) {
      for (const subItem of item.subItems) {
        if (
          subItem.itemType === SidebarItemType.Page &&
          subItem.id &&
          subItem.link
        ) {
          const result = checkLink(
            pathname,
            subItem.link,
            item.id,
            subItem.id,
            isExact
          );
          if (result) {
            return result;
          }
        } else if (
          subItem.itemType === SidebarItemType.Section &&
          subItem.id &&
          subItem.links &&
          subItem.links.length > 0
        ) {
          const result = checkLinks(
            pathname,
            subItem.links,
            item.id,
            subItem.id,
            isExact
          );
          if (result) {
            return result;
          }
        }
      }
    }
  }
  return null;
}

function getCurrentPageInfo(navigationItems) {
  const { pathname } = window.location;

  const exactResult = findMatchingLink(
    pathname,
    navigationItems,
    true
  );
  if (exactResult) {
    return exactResult;
  }

  const partialResult = findMatchingLink(
    pathname,
    navigationItems,
    false
  );
  if (partialResult) {
    return partialResult;
  }

  return {
    notFound: true,
    display: "",
    subDisplay: "",
    openInNewTab: false,
    workspaceId: "",
    reportId: "",
    listName: "",
    recordName: "",
    allowedRoles: [],
    title: "",
    url: "",
  };
}

function getCurrentPageFromGlobalState(globalState) {
  const navItems = getNavigationItemsToShow(globalState);
  return getCurrentPageInfo(navItems);
}

function getNavigationItemsToShow(globalState) {
  const { sitemap } = globalState;
  let navigationItems = [];
  if (sitemap && sitemap.Items && sitemap.Items.length > 0) {
    navigationItems = getSiteMapSectionsAndLinks(sitemap);
  }
  navigationItems = getPagesToAlwaysShow(navigationItems);

  navigationItems = filterNavigationItemsBasedOnRole(
    globalState,
    navigationItems
  );

  return navigationItems;
}

function filterNavigationItemsBasedOnRole(
  globalState,
  navigationItems
) {
  let updatedNavigationItems = [];

  navigationItems.forEach((item) => {
    if (
      item.itemType === SidebarItemType.Page &&
      item.link &&
      checkForValidRoles(item.link.allowedRoles, globalState)
    ) {
      updatedNavigationItems.push(item);
    } else if (
      item.itemType === SidebarItemType.Section &&
      item.subItems &&
      item.subItems.length > 0
    ) {
      let filteredSubItems = [];

      item.subItems.forEach((s) => {
        if (
          s.itemType === SidebarItemType.Page &&
          s.link &&
          checkForValidRoles(s.link.allowedRoles, globalState)
        ) {
          filteredSubItems.push(s);
        } else if (
          s.itemType === SidebarItemType.Section &&
          s.links &&
          s.links.length > 0
        ) {
          let filteredLinks = s.links.filter((l) =>
            checkForValidRoles(l.allowedRoles, globalState)
          );

          if (filteredLinks && filteredLinks.length > 0) {
            filteredSubItems.push({
              id: s.id,
              text: s.text,
              itemType: s.itemType,
              links: filteredLinks,
            });
          }
        }
      });

      if (filteredSubItems && filteredSubItems.length > 0) {
        updatedNavigationItems.push({
          id: item.id,
          text: item.text,
          itemType: item.itemType,
          subItems: filteredSubItems,
        });
      }
    }
  });

  return updatedNavigationItems;
}

//This function will make sure that the pages listed in navigationItemsToAlwaysShow will be in the
//returned navigationItems array in getNavigationItemsToShow before running the check for whether it should be
//displayed. This makes it such that these pages will always appear, even if they weren't set explicitly in the sitemap
function getPagesToAlwaysShow(navigationItems) {
  let updatedNavigationItems = [];

  if (navigationItems) {
    updatedNavigationItems = navigationItems;
  }

  navigationItemsToAlwaysShow.forEach((alwaysItem) => {
    let alwaysItemAlreadyExists = false;

    //Loop through the given navigationItems and see if the alwaysItem link exists within it
    updatedNavigationItems.forEach((item) => {
      if (
        !alwaysItemAlreadyExists &&
        item.itemType === SidebarItemType.Page &&
        item.link &&
        item.link.to &&
        item.link.to === alwaysItem
      ) {
        alwaysItemAlreadyExists = true;
      } else if (
        !alwaysItemAlreadyExists &&
        item.itemType === SidebarItemType.Section &&
        item.subItems !== null &&
        item.subItems.length > 0
      ) {
        item.subItems.forEach((subItem) => {
          if (
            subItem.itemType === SidebarItemType.Page &&
            subItem.link &&
            subItem.link.to === alwaysItem
          ) {
            alwaysItemAlreadyExists = true;
          } else if (
            subItem.itemType === SidebarItemType.Section &&
            subItem.links &&
            subItem.links.length > 0 &&
            subItem.links.some((l) => l.to === alwaysItem)
          ) {
            alwaysItemAlreadyExists = true;
          }
        });
      }
    });

    //If alwaysItem doesn't exist, then simply add it as a new link to the end of the list of navigation items
    if (!alwaysItemAlreadyExists) {
      let pageDetails =
        getNavigationItemToAlwaysDisplayDetails(alwaysItem);
      updatedNavigationItems.push({
        itemType: SidebarItemType.Page,
        id: pageDetails.text,
        text: pageDetails.text,
        link: pageDetails,
      });
    }
  });

  return updatedNavigationItems;
}

function getSiteMapSectionsAndLinks(sitemap) {
  let result = [];

  sitemap.Items.forEach((item) => {
    if (
      item.ItemType === SidebarItemType.Page &&
      item.Page &&
      item.Page.Id &&
      !isSettingsManagedPage(item.Page)
    ) {
      let pageDetails = getNavigationItemDetails(item.Page);
      pageDetails.openInNewTab = item.Page.OpenInNewTab;
      result.push({
        itemType: item.ItemType,
        id: pageDetails.text,
        text: pageDetails.text,
        link: pageDetails,
      });
    } else if (
      item.ItemType === SidebarItemType.Section &&
      item.Section &&
      item.Section.SectionName &&
      item.Section.SectionItems
    ) {
      let subItems = getSiteMapSubItems(item);

      if (subItems && subItems.length > 0) {
        result.push({
          itemType: item.ItemType,
          id: item.Section.SectionName,
          text: item.Section.SectionName,
          subItems: subItems,
        });
      }
    }
  });
  return result;
}

function getSiteMapSubItems(item) {
  let subItems = [];
  item.Section.SectionItems.forEach((sectionItem) => {
    if (
      sectionItem.ItemType === SidebarItemType.Page &&
      sectionItem.Page &&
      !isSettingsManagedPage(sectionItem.Page)
    ) {
      let pageDetails = getNavigationItemDetails(sectionItem.Page);
      pageDetails.openInNewTab = sectionItem.Page.OpenInNewTab;
      subItems.push({
        itemType: sectionItem.ItemType,
        id: pageDetails.text,
        text: pageDetails.text,
        link: pageDetails,
      });
    } else if (
      sectionItem.ItemType === SidebarItemType.Section &&
      sectionItem.SubSection &&
      sectionItem.SubSection.SubsectionName &&
      sectionItem.SubSection.Pages &&
      sectionItem.SubSection.Pages.length > 0
    ) {
      subItems.push({
        itemType: sectionItem.ItemType,
        id: sectionItem.SubSection.SubsectionName,
        text: sectionItem.SubSection.SubsectionName,
        links: getSiteMapLinks(sectionItem.SubSection.Pages),
      });
    }
  });
  return subItems;
}

function getSiteMapLinks(pages) {
  let newLinks = [];
  pages.forEach((page) => {
    if (isSettingsManagedPage(page)) {
      return;
    }

    let newLink = getNavigationItemDetails(page);
    newLink.openInNewTab = page.OpenInNewTab;

    newLinks.push(newLink);
  });
  return newLinks;
}

export {
  getCurrentPageInfo,
  getCurrentPageFromGlobalState,
  getNavigationItemsToShow,
  navigationItemsToAlwaysShow,
};
